import * as React from "react";

import * as R from "ramda";

import GenButton from "../Gen/Button";
import GenLoadingSpinner from "../Gen/LoadingSpinner";
import GenModal from "../Gen/Modal";

import ActivityReviewSummary from "../Activity/ReviewSummary";

import { stringDateToDisplayDateString } from "../../src/time_display";

import { documentDisplayTitle, documentDisplayNumber } from "../../src/document_display_title";

const drawerUidsInOrder = [
  "vitals",
  "differential",
  "workup",
  "diagnosis",
  "outcomes",
  "care",
  "keypoints",
];

function uidIndex(placementUid: string) {
  const foundIndex = drawerUidsInOrder.indexOf(placementUid);
  return foundIndex >= 0 ? foundIndex : drawerUidsInOrder.length;
}

function beforeInsideIndex(placementBefore: boolean, placementInside: boolean) {
  if (placementBefore && !placementInside) {
    return 0;
  } else if (placementBefore && placementInside) {
    return 1;
  } else if (!placementBefore && placementInside) {
    return 2;
  } else {
    return 3;
  }
}

function activityOrderIndices({
  placement_uid, placement_before, placement_inside, order,
}: ActivitySnakeModel) {
  return [uidIndex(placement_uid), beforeInsideIndex(placement_before, placement_inside), order];
}

interface CatalogReviewModalProps {
  afterCancel: () => void
  afterSave: () => void
  allItems: Array<any>
  document: DocumentSnakeModel
  documentSetId: number
  review_submitted: boolean
  setReviewRow: (row: number) => void
  submitted_at: string
  user: UserSnakeModel
  review_comment: string
}

interface CatalogReviewModalState {
  activities?: Array<ActivitySnakeModel>;
  activity_user_states_by_activity_id?: Record<number, ActivityUserStateSnakeModel>
  loading: boolean
  reviewComment: string
  overrideReviewComment?: boolean;
}

export default class CatalogReviewModal extends React.Component<CatalogReviewModalProps, CatalogReviewModalState> {
  constructor(props: CatalogReviewModalProps) {
    super(props);

    const {
      review_comment,
    } = props;

    this.state = {
      reviewComment: review_comment,
      loading: true,
    };
  }

  componentDidMount() {
    this.getData();
  }

  getData() {
    const {
      documentSetId,
      document,
      user,
    } = this.props;

    fetch(`/activity_report/${documentSetId}/${document.id}/${user.id}`, {
      headers: {
        Accept: "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) { throw new Error(`${response.status} response`); }

        return response.json();
      })
      .then((data) => {
        this.setState({
          ...data,
          loading: false,
        });
      })
      .catch(() => { alert("There was an issue loading the data. Please try again."); });
  }

  postDocumentSetDocumentStateData(id: string, data: {review_comment: string, reviewed_at?: string}) {
    const {
      afterSave,
    } = this.props;

    fetch(`/document_set_document_states/${id}`, {
      method: "PUT",
      body: JSON.stringify({document_set_document_state: data}),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) { throw new Error(`${response.status} response`); }

        return response.json();
      })
      .then(() => {
        afterSave();
        this.getData();
      })
      .catch(() => { alert("There was an error saving your review."); });
  }

  render() {
    const {
      activities,
      activity_user_states_by_activity_id,
      loading,
      reviewComment,
      overrideReviewComment,
    } = this.state;

    const {
      afterCancel,
      allItems,
      document,
      documentSetId,
      review_submitted,
      setReviewRow,
      submitted_at,
      user,
    } = this.props;

    let renderReview;
    if (loading || !activities || !activity_user_states_by_activity_id) {
      renderReview = <GenLoadingSpinner />;
    } else {
      const renderedActivities = R.sortBy((activity) => activityOrderIndices(activity) as any, activities).map((activity) => (
        <div key={activity.id} className="mb-3">
          {activity.content.flat()[0]?.type !== "section" && <h2>{activity.name}</h2>}
          <ActivityReviewSummary
            activity={activity}
            activityUserState={activity_user_states_by_activity_id[activity.id]}
            documentMediaFolder={document.folder_url}
          />
          <hr />
        </div>
      ));

      const allowReviewEdit = !review_submitted || !!overrideReviewComment;

      renderReview = (
        <div>
          <div className="review-modal-feedback" style={{marginBottom: "10px"}}>
            <div>
              <p>Feedback:</p>
              <textarea
                style={{resize: "none"}}
                rows={4}
                value={reviewComment}
                disabled={!allowReviewEdit}
                onChange={(e) => { this.setState({reviewComment: e.target.value}); }}
              />
            </div>
            <div>
              <div className="review-buttons">
                <GenButton
                  name={<span><i className="fa-regular fa-floppy-disk pr-1"></i> Save Draft</span>}
                  highlighted
                  disabled={review_submitted}
                  onClick={() => {
                    this.postDocumentSetDocumentStateData(`${documentSetId}/${document.id}/${user.id}`, {
                      review_comment: reviewComment,
                    });
                  }}
                />
                <GenButton
                  name={allowReviewEdit ? <span><i className="fa-regular fa-circle-check pr-1"></i> Submit Review</span> : <span><i className="fa-regular fa-edit pr-1"></i> Change Review</span>}
                  highlighted
                  onClick={() => {
                    if (allowReviewEdit) {
                      this.postDocumentSetDocumentStateData(`${documentSetId}/${document.id}/${user.id}`, {
                        review_comment: reviewComment,
                        reviewed_at: (new Date()).toISOString(),
                      });
                      this.setState({ overrideReviewComment: false })
                    } else {
                      this.setState({ overrideReviewComment: true })
                    }
                  }}
                />
              </div>
            </div>
          </div>

          <hr />

          <div className="activity">
            {renderedActivities}
          </div>
        </div>
      );
    }

    const sumbittedItems = allItems.filter((item) => item.submitted_at);
    const totalReviewableCount = sumbittedItems.length;
    const reviewIndex = sumbittedItems.findIndex((item) => item.user.id === user.id && item.document.id === document.id);

    const reviewNavigation = (
      <div className="flex items-center gap-3">
        <GenButton
          name={<span><i className="fa fa-chevron-left" /> Prev</span>}
          highlighted
          disabled={reviewIndex === 0}
          onClick={() => {
            const newRow = sumbittedItems[reviewIndex - 1];
            setReviewRow(newRow);
            this.setState({
              reviewComment: newRow.review_comment,
              loading: true,
            }, this.getData.bind(this));
          }}
        />
        <div className="border-solid">{reviewIndex + 1}/{totalReviewableCount}</div>
        <GenButton
          name={<span>Next <i className="fa fa-chevron-right" /></span>}
          highlighted
          disabled={reviewIndex === totalReviewableCount - 1}
          onClick={() => {
            const newRow = sumbittedItems[reviewIndex + 1];
            setReviewRow(newRow);
            this.setState({
              reviewComment: newRow.review_comment,
              loading: true,
            }, this.getData.bind(this));
          }}
        />
        <GenButton
          name={<i className="fa fa-xmark" />}
          highlighted
          onClick={afterCancel}
        />
      </div>
    );

    return (
      <GenModal open>
        <div className="gen-modal-panel">
          <div className="review-modal-header relative">
            <div className="flex justify-between items-center">
              <h1>Case {documentDisplayNumber(document)} Review</h1>
              {reviewNavigation}
            </div>

            <p style={{marginBottom: "5px"}}><strong>Case:</strong> <a href={`/documents/${document.id}`} target="_blank" rel="noreferrer">{documentDisplayTitle(document)}</a></p>
            <p style={{marginBottom: "5px"}}><strong>Student&apos;s Name:</strong> {user.first_name} {user.last_name}</p>
            <p style={{marginBottom: "5px"}}><strong>Student&apos;s Email:</strong> {user.email}</p>
            <p style={{marginBottom: "5px"}}><strong>Submission Time:</strong> {stringDateToDisplayDateString(submitted_at)}</p>
          </div>

          <hr />

          {renderReview}
        </div>
      </GenModal>
    );
  }
}
